import {
    AfterViewInit,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    NgZone,
    OnDestroy,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { ChatConversation, ChatMessage, PhitoAiQuestion } from '../documentation-chat';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { AppConfigService } from '../../../../services/app-config.service';
import { BackendApiService } from '../../../../services/backend-api.service';
import { PhitoSessionService } from '../../../../services/phito-session.service';
import { HttpParams } from '@angular/common/http';
import { MarkdownComponent } from '../../../../components/markdown/markdown.component';
import { finalize, Subscription } from 'rxjs';
import { NotificationService } from 'projects/phito-core/src/lib/services/notification.service';
import { MatTooltip } from '@angular/material/tooltip';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatBadge } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import {
    ChatSourcesDialogComponent,
    ChatSourcesDialogInput,
} from './chat-sources-dialog/chat-sources-dialog.component';
import { DatePipe } from '@angular/common';

interface ChatEventMessage {
    eventType: ChatEventType;
    conversationHexId: string;
    aiMessage: string;
}

type ChatEventType =
    | 'PARTIAL_RESPONSE'
    | 'PARTIAL_THINKING'
    | 'INTERMEDIATE_RESPONSE'
    | 'CONTENT_FETCHED'
    | 'BEFORE_TOOL_EXECUTION'
    | 'TOOL_EXECUTED'
    | 'CHAT_COMPLETED'
    | 'TITLE_GENERATED'
    | 'UNDEFINED';

@Component({
    selector: 'phito-chat-window',
    imports: [
        FormsModule,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatInput,
        MatSuffix,
        ReactiveFormsModule,
        MarkdownComponent,
        MatTooltip,
        MatBadge,
        DatePipe
    ],
    templateUrl: './chat-window.component.html',
    styleUrl: './chat-window.component.scss',
})
export class ChatWindowComponent implements AfterViewInit, OnDestroy {
    private appConfig = inject(AppConfigService);
    private ngZone = inject(NgZone);
    private api = inject(BackendApiService);
    private sessionService = inject(PhitoSessionService);
    private notificationService = inject(NotificationService);
    private clpbrd = inject(Clipboard);
    private dialog = inject(MatDialog);

    @ViewChild('chatOutput')
    private chatOutput?: ElementRef<HTMLElement>;

    conversation = input<ChatConversation>();
    private activeConversation = signal<ChatConversation | undefined>(undefined);
    titleChanged = output<string>();

    private viewReady = signal(false);
    socket: WebSocket;
    socketOpen = signal(false);
    destroying = signal(false);

    private streamingAiMessage?: ChatMessage;

    messages = signal<ChatMessage[]>([]);

    isProcessing = signal(false);
    connectionError = signal(false);
    isLoadingMessages = signal(false);
    isSavingConversation = signal(false);

    private messagesSubscription?: Subscription;

    inputDisabled = computed(
        () =>
            this.isProcessing() ||
            this.isSavingConversation() ||
            !this.socketOpen() ||
            this.connectionError(),
    );

    questionCtrl = new FormControl<string>('', { nonNullable: true });

    constructor() {
        this.initializeSocket();

        effect(() => {
            this.activeConversation.set(this.conversation());
            this.loadMessages(this.conversation()?.id);
        });

        effect(() => {
            if (this.inputDisabled()) {
                this.questionCtrl.disable();
            } else {
                this.questionCtrl.enable();
            }
        });

        effect(() => {
            if (!this.viewReady()) {
                return;
            }

            this.messages();
            this.scheduleScrollToBottom();
        });
    }

    ngAfterViewInit(): void {
        this.viewReady.set(true);
        this.scheduleScrollToBottom();
    }

    private initializeSocket(): void {
        const baseUrl = this.appConfig.getConfig().WS_BASE_URL;

        if (!baseUrl) {
            this.connectionError.set(true);
            return;
        }

        const token = this.sessionService?.xsrfToken();
        if (!token) {
            return;
        }

        const socketUrl = baseUrl + 'ws/chat/' + token;

        try {
            this.socket = new WebSocket(socketUrl);
        } catch (error) {
            console.error('Error creating chat websocket connection', error);
            this.connectionError.set(true);
            return;
        }

        this.socket.onopen = () => {
            this.ngZone.run(() => {
                this.socketOpen.set(true);
                this.connectionError.set(false);
            });
        };

        this.socket.onmessage = (event: MessageEvent<string>) => {
            this.ngZone.run(() => this.handleMessage(event));
        };

        this.socket.onerror = () => {
            this.ngZone.run(() => {
                this.connectionError.set(true);
                this.socketOpen.set(false);
                this.isProcessing.set(false);
            });
        };

        this.socket.onclose = () => {
            this.ngZone.run(() => {
                this.socketOpen.set(false);
                this.isProcessing.set(false);
                this.socket = undefined;

                if (!this.destroying() && !this.connectionError()) {
                    this.initializeSocket();
                }
            });
        };
    }

    private handleMessage(event: MessageEvent<string>): void {
        if (!event.data) {
            return;
        }

        try {
            const payload = JSON.parse(event.data) as ChatEventMessage;

            switch (payload.eventType) {
                case 'CHAT_COMPLETED':
                    this.isProcessing.set(false);
                    if (this.streamingAiMessage) {
                        this.streamingAiMessage.text = payload.aiMessage;
                    }
                    this.streamingAiMessage = undefined;
                    this.loadMessages(payload.conversationHexId);
                    this.scheduleScrollToBottom();
                    break;
                case 'PARTIAL_RESPONSE':
                    if (!this.streamingAiMessage) {
                        const aiMessage = new ChatMessage();
                        aiMessage.text = '';
                        aiMessage.type = 'AI';
                        this.streamingAiMessage = aiMessage;
                        this.messages.set([...this.messages(), aiMessage]);
                    }

                    this.streamingAiMessage.text = (this.streamingAiMessage.text || '') + payload.aiMessage;
                    this.scheduleScrollToBottom();
                    break;
                case 'TITLE_GENERATED':
                    this.titleChanged.emit(payload.aiMessage);
                    break;
                default:
                    console.log(payload);
                    break;
            }
        } catch (error) {
            console.error('Unable to parse chat event message', error);
        }
    }

    private loadMessages(conversationHexId: string): void {
        this.isLoadingMessages.set(true);

        let params = new HttpParams().set('orderby', 'index::asc');
        params = params.append('filter', 'conversationHexId::eq::' + conversationHexId);

        this.messagesSubscription?.unsubscribe();
        this.messagesSubscription = this.api
            .getAll<ChatMessage>('chatmessages', params)
            .pipe(finalize(() => this.isLoadingMessages.set(false)))
            .subscribe({
                next: (response) => {
                    this.messages.set(response?.data ?? []);
                },
                error: (err) => {
                    this.notificationService.errorMessageGet('Fehler!', err, params);
                    this.messages.set([]);
                },
            });
    }

    submitQuestion(): void {
        if (this.inputDisabled()) {
            return;
        }

        const question = this.questionCtrl.value.trim();
        if (!question) {
            return;
        }

        this.questionCtrl.setValue('', { emitEvent: false });

        const conversation = this.activeConversation();
        if (!conversation || !conversation.id) {

            this.isSavingConversation.set(true);
            this.api
                .create<ChatConversation>('conversations', new ChatConversation())
                .pipe(finalize(() => this.isSavingConversation.set(false)))
                .subscribe({
                    next: (response) => {
                        this.activeConversation.set(response);
                        this.messages.set([]);
                        this.sendQuestion(response, question);
                    },
                    error: (error) => {
                        console.error('Failed to create conversation', error);
                    },
                });
            return;
        }

        this.sendQuestion(conversation, question);
    }

    ngOnDestroy(): void {
        this.destroying.set(true);
        this.messagesSubscription?.unsubscribe();
        this.socket?.close();
    }

    private scheduleScrollToBottom(): void {
        const output = this.chatOutput?.nativeElement;
        if (!output) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
                output.scrollTop = output.scrollHeight;
            });
        });
    }

    private sendQuestion(conversation: ChatConversation, question: string): void {
        this.isProcessing.set(true);

        const pq = new PhitoAiQuestion();
        pq.question = question;
        pq.conversationHexId = conversation.id;

        this.socket?.send(JSON.stringify(pq));

        const userMessage = new ChatMessage();
        userMessage.text = question;
        userMessage.type = 'USER';
        this.messages.set([...this.messages(), userMessage]);

        const aiMessage = new ChatMessage();
        aiMessage.text = '';
        aiMessage.type = 'AI';
        this.streamingAiMessage = aiMessage;
        this.messages.set([...this.messages(), aiMessage]);
    }

    copyContent(message: ChatMessage) {
        if (this.clpbrd.copy(message.text)) {
            this.notificationService.infoMessage('Text in Zwischenablage gespeichert!');
        }
    }

    openSourcesDialog(message: ChatMessage): void {
        this.dialog.open(ChatSourcesDialogComponent, {
            data: new ChatSourcesDialogInput(message),
        });
    }

    like(index: number) {
        const message = this.messages()[index];
        if (message.like === true) {
            message.like = null;
        } else {
            message.like = true;
        }
        this.updateMessage(message, index);
    }

    dislike(index: number) {
        const message = this.messages()[index];
        if (message.like === false) {
            message.like = null;
        } else {
            message.like = false;
        }
        this.updateMessage(message, index);
    }

    updateMessage(message: ChatMessage, index: number) {
        this.api.update('chatmessages', message).subscribe({
            next: (resp: ChatMessage) => {
                this.messages().splice(index, 1, resp);
                this.notificationService.infoMessage('Erfolgreich gespeichert!');
            },
            error: err => {
                this.notificationService.errorMessagePut('Fehler!', err, message);
                this.loadMessages(message.conversationHexId);
            }
        });
    }

    getSourceCount(message: ChatMessage): number {
        let i = 0;
        for (const s of message.sources) {
            if (s.relatedApiPath === 'documents' || (!s.relatedApiPath &&
                (s.apiPath === 'documentations' || s.apiPath === 'maintenances'))) {
                i++;
            }
        }
        return i;
    }

}
